'use client'

import { useEffect, useRef, useState, useSyncExternalStore } from 'react'

import {
  AtSign, CalendarClock, Image, MapPin, Music,
  ShoppingBag, Ticket, Video, type LucideIcon,
} from 'lucide-react'

import type { PreviewIcon, PreviewProfile } from './persona-previews'

/**
 * Names back to components, on the client.
 *
 * The preview tables are built on the server and cross into this Client
 * Component, and React can only carry plain data over that boundary: handing it
 * the imported icon components throws "Only plain objects can be passed to
 * Client Components". So the data carries a name and the mapping lives here.
 */
const PREVIEW_ICONS: Record<PreviewIcon, LucideIcon> = {
  audio: Music,
  image: Image,
  social: AtSign,
  video: Video,
  product: ShoppingBag,
  ticket: Ticket,
  booking: CalendarClock,
  map: MapPin,
}

/**
 * Three sample pages, stacked, cycling front to back.
 *
 * The hero used to draw one grey wireframe, which showed the shape of a Fuse
 * page and nothing about what one can look like. A rotating stack of finished
 * pages makes the argument the copy is making: the space is yours to design.
 *
 * Built on transforms and opacity only, so the whole cycle runs on the
 * compositor. Positions are derived from `(index - front + n) % n`, so a card
 * never needs to know where it was, only which depth it is at now.
 *
 * The animation is decorative. It pauses when the tab is hidden, when it scrolls
 * out of view, and when the visitor has asked for reduced motion, in which case
 * the front page is simply shown and never moves.
 */

const INTERVAL = 3000

/**
 * Depth positions. Index 0 is the front card.
 *
 * The cards fan to the RIGHT rather than upward: stacking on `y` hid the whole
 * offset behind the front card's own frame, so all that showed was a sliver of
 * two rounded tops poking over the phone. Offsetting on `x` puts each card
 * beside the one in front of it, where the stack reads as depth.
 */
type Depth = { x: number, y: number, scale: number, opacity: number }

const DEPTHS: Depth[] = [
  { x: 0, y: 0, scale: 1, opacity: 1 },
  { x: 72, y: 0, scale: 0.94, opacity: 0.7 },
  { x: 130, y: 0, scale: 0.88, opacity: 0.45 },
]

/**
 * The same stack, fanned downward.
 *
 * On a phone the hero is one column, so the cards have width to spare and no
 * room beside them: a rightward fan would either overflow the screen or shrink
 * the front card to fit. Downward puts the depth where the space is. The
 * offsets are positive, so the back cards sit *below* the front one and the
 * page's own reading order still starts at the card in front.
 */
const DEPTHS_STACKED: Depth[] = [
  { x: 0, y: 0, scale: 1, opacity: 1 },
  { x: 0, y: 26, scale: 0.95, opacity: 0.7 },
  { x: 0, y: 48, scale: 0.9, opacity: 0.45 },
]

/** The hero is one column below `lg`, which is where the fan has to change. */
const STACK_QUERY = '(max-width: 1023px)'

function subscribeNarrow(onChange: () => void) {
  const query = window.matchMedia(STACK_QUERY)
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}

function readNarrow() {
  return window.matchMedia(STACK_QUERY).matches
}

function subscribeMotion(onChange: () => void) {
  const query = window.matchMedia('(prefers-reduced-motion: reduce)')
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}

function readMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

const PhoneStack = ({
  profiles,
  force,
  frameWidth,
}: {
  profiles: PreviewProfile[]
  /**
   * Pin the arrangement instead of reading the viewport.
   *
   * The viewport query is right for the hero, which spans the screen, and wrong
   * inside a bento card: there the window being wide says nothing about the
   * width the stack actually has, and it fanned the phones sideways into a
   * narrow box, squashing and clipping them.
   */
  force?: 'stacked'
  /** Overrides the frame width, for a caller with its own space to fill. */
  frameWidth?: string
}) => {
  const [front, setFront] = useState(0)
  const [active, setActive] = useState(true)
  const hostRef = useRef<HTMLDivElement>(null)

  // Subscribed rather than read into state in an effect, so the first paint
  // already knows the answer. Writing it from an effect would start the cycle
  // for one frame before stopping it, which is exactly the motion the setting
  // asks us not to produce. The server has no media queries, so it renders the
  // moving version and the client corrects on hydration.
  const still = useSyncExternalStore(subscribeMotion, readMotion, () => false)

  // Same subscribe-don't-effect reasoning as `still`: the server has no media
  // queries, so it renders the wide arrangement and the client corrects on
  // hydration. Reading it in an effect instead would fan right for one frame
  // on every phone.
  const viewportStacked = useSyncExternalStore(subscribeNarrow, readNarrow, () => false)
  const stacked = force === 'stacked' || viewportStacked

  // Off-screen and background tabs stop the timer. An animation nobody is
  // looking at is pure battery cost, and on a phone this is the first screen so
  // it would otherwise run the whole time somebody reads the page below it.
  useEffect(() => {
    const host = hostRef.current
    if(!host) return

    let visible = true
    let onScreen = true
    const sync = () => setActive(visible && onScreen)

    const onVisibility = () => {
      visible = document.visibilityState === 'visible'
      sync()
    }
    document.addEventListener('visibilitychange', onVisibility)

    const observer = new IntersectionObserver(
      entries => {
        onScreen = entries[0]?.isIntersecting ?? true
        sync()
      },
      { threshold: 0.2 },
    )
    observer.observe(host)

    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    if(still || !active || profiles.length < 2) return
    const id = window.setInterval(() => {
      setFront(current => (current + 1) % profiles.length)
    }, INTERVAL)
    return () => window.clearInterval(id)
  }, [still, active, profiles.length])

  return (
    <div
      ref={hostRef}
      // Room for the fan to grow into, or the two back cards are clipped by the
      // section rather than by anything intentional: below `lg` that is height
      // underneath, and from `lg` it is width to the right, with the max width
      // covering the front card plus that overhang.
      className={
        frameWidth
          ? `relative mx-auto w-full ${frameWidth} pb-10`
          : 'relative mx-auto w-full max-w-[19rem] pb-14 lg:mx-0 lg:max-w-[26rem] lg:pr-32 lg:pb-0'
      }
    >
      {/* Height comes from a non-positioned copy of the frame: three absolute
          cards would collapse the parent to nothing, and a hard-coded height
          would drift the moment a card's padding changed. Every preview is
          three blocks with one of them wide, so all three frames are the same
          height and any of them can do the measuring. */}
      <div className={frameWidth ? 'invisible w-full' : 'invisible w-full lg:w-[calc(100%-8rem)]'} aria-hidden='true'>
        <PhoneFrame profile={profiles[0]} />
      </div>

      {profiles.map((profile, index) => {
        const depth = (index - front + profiles.length) % profiles.length
        const table = stacked ? DEPTHS_STACKED : DEPTHS
        const position = table[Math.min(depth, table.length - 1)]
        const isFront = depth === 0

        return (
          <div
            key={profile.handle}
            // Pinned at the top-left and left to size itself. Stretching it
            // with `inset-y-0` would fight the frame's own height, and the
            // reserved box below is already exactly one frame tall.
            className={frameWidth ? 'absolute left-0 top-0 w-full' : 'absolute left-0 top-0 w-full lg:w-[calc(100%-8rem)]'}
            style={{
              transform: `translate(${position.x}px, ${position.y}px) scale(${position.scale})`,
              // Scaled from the edge the fan grows from, so a card's own shrink
              // does not pull it back toward the front card and undo the
              // offset: the left edge when fanning right, the top when down.
              transformOrigin: stacked ? 'top center' : 'center left',
              opacity: position.opacity,
              // The front card sits above the others; the rest keep their order
              // so the second is always drawn over the third.
              zIndex: profiles.length - depth,
              transition: still ? undefined : 'transform 700ms cubic-bezier(0.22, 1, 0.36, 1), opacity 700ms ease',
            }}
            // Only the front page is announced. The two behind are the same kind
            // of object and reading all three would just repeat the pitch.
            aria-hidden={!isFront}
          >
            <PhoneFrame profile={profile} />
          </div>
        )
      })}
    </div>
  )
}

/** One page, drawn. Matches the real profile's geometry: two columns, 12px gap. */
const PhoneFrame = ({ profile }: { profile: PreviewProfile }) => {
  return (
    <div className='w-full rounded-[2.5rem] border-[10px] border-border bg-background shadow-2xl'>
      <div className={`overflow-hidden rounded-[1.75rem] ${profile.page} ${profile.pageText ?? 'text-white'}`}>
        <div className='flex flex-col items-center gap-2.5 px-4 pb-1 pt-6 text-center'>
          <div className={`size-14 rounded-full ${profile.avatar}`} />
          <div className='space-y-0.5'>
            <p className='font-gilroy text-sm font-bold'>{profile.name}</p>
            <p className='text-[11px] font-medium opacity-60'>{profile.handle}</p>
          </div>
        </div>

        {/* `gap-3` is 12px, the real MOBILE_GAP, over the real 2 columns. */}
        <div className='grid grid-cols-2 gap-3 p-4'>
          {profile.blocks.map((block, index) => {
            const Icon = PREVIEW_ICONS[block.icon]
            return (
            <div
              key={index}
              // A 1x1 is square; a 2x1 spans both columns plus the gap between
              // them but stays one row tall, so it is wider than 2:1 rather
              // than taller. `aspect-[2/1]` made it a tall slab, which is the
              // opposite of what the real grid draws.
              className={`flex flex-col justify-between rounded-2xl border border-white/10 p-3 ${
                block.surface
              } ${block.wide ? 'col-span-2 aspect-[2.1/1]' : 'aspect-square'} ${
                block.onDark ? 'text-white' : ''
              }`}
            >
              <span
                className={`flex size-7 items-center justify-center rounded-lg ${
                  block.onDark ? 'bg-white/20 text-white' : 'bg-black/10 text-current'
                }`}
              >
                <Icon className='size-4' aria-hidden='true' />
              </span>
              <span className='mt-3 truncate font-gilroy text-[11px] font-semibold'>{block.label}</span>
            </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default PhoneStack
